import { ShortDriver } from '@allocab/allocab-driver-shared-types';
import { CircleCheckSolid } from '@allocab/allocab-icons';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { AddDriverToVehicleModal } from './_authenticated._main.vehicle/AddDriverToVehicleModal';
import { DeleteVehicleModal } from './_authenticated._main.vehicle/DeleteVehicleModal';
import { RemoveDriverFromVehicleModal } from './_authenticated._main.vehicle/RemoveDriverFromVehicleModal';
import { VehicleDocuments } from './_authenticated._main.vehicle/VehicleDocuments';
import { VehicleInfo } from './_authenticated._main.vehicle/VehicleInfo';
import { VehicleOptionsForm } from './_authenticated._main.vehicle/VehicleOptionsForm';
import { Link } from '../components/atomic/Link';
import { BadgeIndicator } from '../components/BadgeIndicator';
import { DangerButton } from '../components/button/DangerButton';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { LicensePlate } from '../components/LicensePlate';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/table/Table';
import { TableColumn } from '../components/table/types';
import { DRIVERS_LIMIT, prefetchDriversPage } from '../hooks/useFetchDrivers';
import { useFethDriversForVehiclePage } from '../hooks/useFetchDriversForVehicle';
import { useFetchVehicle } from '../hooks/useFetchVehicle';
import { t } from '../services/translation/translation';
import { getFullName } from '../utils/common';
import { getPanelImage } from '../utils/vehicle';

const columns: TableColumn<ShortDriver>[] = [
  {
    name: t('common.name'),
    render: driver => <Link to={`/drivers/${driver.id}`}>{getFullName(driver.firstName, driver.lastName)}</Link>,
    sortKey: 'firstName',
  },
  {
    name: t('common.current-vehicle'),
    render: driver =>
      driver.current ? (
        <CircleCheckSolid width={16} height={16} className="fill-green-700 dark:fill-green-600" />
      ) : null,
    sortKey: 'current',
  },
];

const isDriverSelectable = (driver: ShortDriver) => !driver.current;

export const VehicleRoute = () => {
  const { vehicleKey } = useParams();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState<ShortDriver[]>([]);

  invariant(vehicleKey);

  const { data: vehicle, isLoading } = useFetchVehicle(vehicleKey);

  const [searchParams] = useSearchParams();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? undefined;
  const direction = (searchParams.get('direction') ?? undefined) as 'desc' | 'asc' | undefined;
  const { data: drivers, isLoading: isLoadingDrivers } = useFethDriversForVehiclePage(
    vehicleKey,
    { direction, orderBy },
    currentPage,
  );
  const nbPages = Math.ceil((drivers?.totalItems ?? 0) / DRIVERS_LIMIT);

  useEffect(() => {
    prefetchDriversPage({ direction: 'asc', orderBy: 'firstName', limit: 100 }, 1);
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div>
        <h3 className="text-3xl font-bold dark:text-white mb-6">{t('common.error')}</h3>
        <div className="inline-block space-y-2">
          <div className="h-96 w-96 bg-confused-light dark:bg-confused-dark bg-no-repeat bg-contain bg-center" />
          <p className="text-center">{t('vehicle-route.not-found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{vehicle.licensePlate} - Allocab</title>
      </Helmet>
      <div className="flex items-center gap-2">
        <h3 className="text-3xl font-bold dark:text-white capitalize">
          {`${vehicle.brand?.toLowerCase() ?? ''} ${vehicle.range?.toLowerCase() ?? ''}`}
        </h3>
        <BadgeIndicator variant={vehicle.validated ? 'green' : 'yellow'}>
          {vehicle.validated ? t('common.validated') : t('common.validating')}
        </BadgeIndicator>
        <DangerButton text={t('common.delete')} onClick={() => setIsDeleteModalVisible(true)} />
        {isDeleteModalVisible ? (
          <DeleteVehicleModal onClose={() => setIsDeleteModalVisible(false)} vehicleId={vehicle.id} />
        ) : null}
      </div>
      <div className="mb-6 mt-1">
        <LicensePlate plateNumber={vehicle.licensePlate} />
      </div>
      <div className="mb-4 flex items-start flex-wrap gap-4">
        <img className="w-72 mr-2 self-center" src={getPanelImage(vehicle.panelKeys)} alt="Voiture" />
        <div className="self-center">
          <VehicleInfo vehicle={vehicle} />
        </div>
        <VehicleOptionsForm vehicle={vehicle} />
        <VehicleDocuments vehicleId={vehicle.id} />
      </div>

      <Table
        identifier="id"
        title={t('vehicle-route.access-admin')}
        caption={t('vehicle-route.drivers-list-description')}
        columns={columns}
        data={drivers?.items}
        isLoading={isLoadingDrivers}
        action={
          <>
            <PrimaryButton text={t('common.add')} onClick={() => setIsAddModalVisible(true)} />
            {isAddModalVisible ? (
              <AddDriverToVehicleModal
                onClose={() => setIsAddModalVisible(false)}
                vehicleId={vehicle.id}
                filterDriverKeys={drivers?.items.map(d => d.id) ?? []}
              />
            ) : null}
          </>
        }
        selectedItems={selectedDrivers}
        onSelectedItemsChange={setSelectedDrivers}
        selectionAction={
          <>
            <DangerButton text={t('common.remove')} onClick={() => setIsRemoveModalVisible(true)} />
            {isRemoveModalVisible ? (
              <RemoveDriverFromVehicleModal
                onClose={() => setIsRemoveModalVisible(false)}
                vehicleId={vehicle.id}
                driverKeys={selectedDrivers.map(d => d.id)}
                resetSelection={() => setSelectedDrivers([])}
              />
            ) : null}
          </>
        }
        isSelectable={isDriverSelectable}
        context={undefined}
        totalItems={drivers?.totalItems ?? 0}
        nbPages={nbPages}
      />
    </div>
  );
};
